import React from 'react';
import ReactDOM from 'react-dom';
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from 'react-simple-maps';
import styled, { useTheme, keyframes } from 'styled-components/macro';
import { valueToPercent, SPACINGS, toFixedString } from '../../utils';
import { usePopper } from 'react-popper';
import { Text } from '../../typography';
import geoMap from './world-110m.json';
import { useTranslate } from '@federico.moretti/react-translate';

const GeoAllocationWrapper = styled.div`
  .geography {
    :focus {
      outline: none;
    }
  }
`;
const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const PopperWrapper = styled.div`
  opacity: 0;
  animation: ${fadeIn} 0.1s ease-in 0.1s forwards;
  z-index: ${(p) => p.theme.zIndexes.overlay};
  pointer-events: none;
`;

const Arrow = styled.div`
  position: absolute;
  width: ${SPACINGS[4]};
  height: ${SPACINGS[4]};
  z-index: -1;

  &::before {
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    width: ${SPACINGS[4]};
    height: ${SPACINGS[4]};
    z-index: -1;
    content: '';
    transform: rotate(45deg);
    border: 1px solid ${(p) => p.theme.colors.secondary90w};
    background-color: ${(p) => p.theme.colors.white};
    z-index: ${(p) => p.theme.zIndexes.overlay};
  }

  &[data-placement*='top'] {
    bottom: -${SPACINGS[2]};
  }

  &[data-placement*='right'] {
    left: -${SPACINGS[2]};
  }

  &[data-placement*='bottom'] {
    top: -${SPACINGS[2]};
  }

  &[data-placement*='left'] {
    right: -${SPACINGS[2]};
  }
`;

export const TooltipWrapper = styled.div`
  display: block;
  padding: ${SPACINGS[4]};
  max-width: 490px;
  border-radius: 2px;
  border: 1px solid ${(p) => p.theme.colors.secondary90w};
  background-color: ${(p) => p.theme.colors.white};
  text-align: start;
`;

type GeoItem = {
  label: string;
  value: number;
  position: [number, number] | undefined;
};

type GeoAllocationChartProps = {
  data: GeoItem[];
  projection?: 'geoMercator' | 'geoEqualEarth';
  style?: React.CSSProperties;
  testid?: string;
};

function GeoMarker(
  props: GeoItem & { color: string; textColor: string; language: string }
) {
  const { position, value, label, color, textColor, language } = props;

  const [referenceElement, setReferenceElement] = React.useState(null as any);
  const [popperElement, setPopperElement] = React.useState(null as any);
  const [arrowElement, setArrowElement] = React.useState(null as any);
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'auto',
    modifiers: [
      { name: 'offset', options: { offset: [0, 15] } },
      { name: 'arrow', options: { element: arrowElement } },
      { name: 'flip', enabled: true },
      { name: 'preventOverflow', enabled: true },
      { name: 'hide', enabled: false },
    ],
  });

  const [isHovered, setHovered] = React.useState(false);

  const radius = valueToPercent(value, 0, 1) * 0.8;

  return (
    <>
      <Marker
        coordinates={position}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <circle opacity={0.2} r={radius} fill={color} />
        <circle opacity={0.6} r={radius} stroke={color} fill="transparent" />
        <circle ref={setReferenceElement} r={4} fill={color} />
      </Marker>
      {isHovered &&
        ReactDOM.createPortal(
          <PopperWrapper
            ref={setPopperElement}
            style={styles.popper}
            {...attributes.popper}
          >
            <TooltipWrapper>
              <Text style={{ color: textColor }} size="sm" margin="none">
                {label}: {toFixedString(value * 100, language)}%
              </Text>
            </TooltipWrapper>
            <Arrow
              ref={setArrowElement}
              style={styles.arrow}
              data-placement={attributes?.popper?.['data-popper-placement']}
            />
          </PopperWrapper>,
          document.body
        )}
    </>
  );
}

function GeoAllocationChart(props: GeoAllocationChartProps) {
  const { data, projection = 'geoEqualEarth', testid, style } = props;
  const theme = useTheme();
  const { language } = useTranslate();

  const sortedData = React.useMemo(() => {
    return data.sort((a, b) => (a.value > b.value ? -1 : 1));
  }, [data]);

  return (
    <GeoAllocationWrapper data-testid={testid} style={style}>
      <ComposableMap width={800} height={500} projection={projection}>
        <Geographies geography={geoMap}>
          {({ geographies }) =>
            geographies.map((geo) => (
              <Geography
                className="geography"
                key={geo.rsmKey}
                geography={geo}
                fill={theme.colors.secondary80w}
                stroke={theme.colors.secondary90w}
              />
            ))
          }
        </Geographies>
        {sortedData.map(
          (item, i) =>
            item.position && (
              <GeoMarker
                key={i}
                {...item}
                textColor={theme.colors.secondary40w}
                color={theme.colors.primary}
                language={language}
              />
            )
        )}
      </ComposableMap>
    </GeoAllocationWrapper>
  );
}

export default GeoAllocationChart;
